import { useEffect, useState } from 'react';
import { MapPin, Loader2 } from '@/components/icons';
import { cn } from '@/lib/utils';

interface GeocodingOverlayProps {
  isActive: boolean;
  current: number;
  total: number;
}

export const GeocodingOverlay = ({ isActive, current, total }: GeocodingOverlayProps) => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    if (isActive) {
      setIsVisible(true);
    } else {
      // Delay hiding to allow for smooth transition
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isActive]);

  if (!isVisible) return null;

  const progress = total > 0 ? Math.round((current / total) * 100) : 0;

  return (
    <div 
      className={cn(
        "fixed inset-0 z-50 flex items-center justify-center transition-all duration-300",
        isActive ? "opacity-100" : "opacity-0 pointer-events-none"
      )}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" />
      
      {/* Content Card */}
      <div className={cn(
        "relative mx-4 w-full max-w-md rounded-lg bg-card p-8 text-card-foreground shadow-2xl transition-all duration-500",
        "animate-in zoom-in-95",
        isActive ? "scale-100" : "scale-95"
      )}>
        {/* Pulsing animation wrapper */}
        <div className="animate-pulse">
          {/* Icon and Spinner */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <MapPin className="w-16 h-16 text-blue-500" />
              <Loader2 className="absolute -right-2 -bottom-2 w-8 h-8 text-blue-600 animate-spin" />
            </div>
          </div>
          
          {/* Main Message */}
          <h2 className="mb-2 text-center text-xl font-semibold text-foreground">
            Locating Addresses
          </h2>
          
          <p className="mb-6 text-center text-muted-foreground">
            Please wait while we pinpoint locations on the map...
          </p>
        </div>
        
        {/* Progress Information */}
        <div className="space-y-3">
          {/* Progress Bar */}
          <div className="h-3 w-full overflow-hidden rounded-full bg-muted">
            <div 
              className="bg-blue-500 h-full rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          
          {/* Progress Text */}
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Processing address {current} of {total}</span>
            <span>{progress}%</span>
          </div>
        </div>
        
        {/* Additional Info */}
        <p className="mt-4 text-center text-xs text-muted-foreground">
          This may take a moment depending on the number of addresses
        </p>
      </div>
    </div>
  );
};
